// dashboard/index.tsx
import React, { useState, useEffect } from 'react';
import ReusableButton from '../ui/button';
import { useDriveStore } from '../drive-store';
import { docTypeMap } from '../files/table/table';
import { useNavigate } from 'react-router-dom';
import { DashboardStats } from './DashboardStats';
import { AlertBanner } from './AlertBanner';
import { ComingSoonModal } from './ComingSoonModal';
import { fetchDocumentStatus, getStatusDisplayText, getStatusBadgeClasses, fetchPartyDocuments, fetchDocumentTypes, DocumentType, getDocumentTypeName, fetchDocumentTypeByDiskPrefix } from '../services/signature-api';

type DashboardCardProps = {
  title: string;
  children: React.ReactNode;
};

export const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  children,
}) => {
  return (
    <div className="flex-1 rounded-2xl border border-gray-200 bg-white p-[15px] shadow-sm hover:shadow-md transition-shadow duration-200 md:p-[25px]">
      <h2 className="text-[18px] font-semibold text-gray-800 mb-16 pb-12 border-b border-gray-100">{title}</h2>
      {children}
    </div>
  );
};

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const documentsFromStore = useDriveStore(s => s.documents);

  // State for Coming Soon modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: '',
    message: '',
    icon: 'wallet' as 'wallet' | 'plus' | 'shield'
  });

  // State for document statuses
  const [documentStatuses, setDocumentStatuses] = useState<Record<string, string>>({});

  // State for signature documents
  const [signatureDocuments, setSignatureDocuments] = useState<any[]>([]);
  const [partyId, setPartyId] = useState<string | null>(null);

  // State for document types by disk prefix
  const [documentTypesByDiskPrefix, setDocumentTypesByDiskPrefix] = useState<Record<string, string>>({});
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);

  const documents = documentsFromStore || { file_entries: [], template_data: [] };
  const fileEntries = Array.isArray(documents.file_entries) ? documents.file_entries : [];
  const templateData = Array.isArray(documents.template_data) ? documents.template_data : [];

  // Fetch document types on component mount
  useEffect(() => {
    const loadDocumentTypes = async () => {
      try {
        const types = await fetchDocumentTypes();
        setDocumentTypes(types);
      } catch (error) {
        console.error('Failed to load document types:', error);
        setDocumentTypes([]);
      }
    };

    loadDocumentTypes();
  }, []);

  // Fetch party_id and signature documents on mount
  useEffect(() => {
    const fetchPartyIdAndDocuments = async () => {
      // Get party_id from localStorage
      const storedPartyId = localStorage.getItem('party_id');
      if (storedPartyId) {
        setPartyId(storedPartyId);

        try {
          const response = await fetchPartyDocuments(storedPartyId);
          if (response.status === 'success' && response.documents) {
            setSignatureDocuments(response.documents);
          } else {
            setSignatureDocuments([]);
          }
        } catch (error) {
          console.error('Error fetching signature documents:', error);
          setSignatureDocuments([]);
        }
      }
    };

    fetchPartyIdAndDocuments();
  }, []);

  // Fetch document statuses from API
  useEffect(() => {
    const fetchStatuses = async () => {
      if (signatureDocuments.length === 0) return;

      const statusPromises = signatureDocuments.map(async (doc: any) => {
        try {
          const documentId = doc.document_id;
          if (!documentId) return null;

          const response = await fetchDocumentStatus(documentId);
          return {
            documentId,
            status: response.data.document_status
          };
        } catch (error) {
          console.warn(`Failed to fetch status for document ${doc.document_id}:`, error);
          return null;
        }
      });

      const results = await Promise.all(statusPromises);
      const statusMap: Record<string, string> = {};

      results.forEach(result => {
        if (result) {
          statusMap[result.documentId] = result.status;
        }
      });

      setDocumentStatuses(statusMap);
    };

    fetchStatuses();
  }, [signatureDocuments]);

  // Fetch document statuses from API
  useEffect(() => {
    const fetchStatuses = async () => {
      if (signatureDocuments.length === 0) return;

      const statusPromises = signatureDocuments.map(async (doc: any) => {
        try {
          const documentId = doc.document_id;
          if (!documentId) return null;

          const response = await fetchDocumentStatus(documentId);
          return {
            documentId,
            status: response.data.document_status
          };
        } catch (error) {
          console.warn(`Failed to fetch status for document ${doc.document_id}:`, error);
          return null;
        }
      });

      const results = await Promise.all(statusPromises);
      const statusMap: Record<string, string> = {};

      results.forEach(result => {
        if (result) {
          statusMap[result.documentId] = result.status;
        }
      });
      setDocumentStatuses(statusMap);
    };

    fetchStatuses();
  }, [signatureDocuments]);

  // Fetch document types by disk prefix
  useEffect(() => {
    const fetchDocTypes = async () => {
      if (signatureDocuments.length === 0) return;

      const typePromises = signatureDocuments.map(async (doc: any) => {
        const diskPrefix = doc.docprefix || doc.disk_prefix;
        if (!diskPrefix) return null;

        try {
          const docType = await fetchDocumentTypeByDiskPrefix(diskPrefix);
          return {
            diskPrefix: diskPrefix,
            documentType: docType
          };
        } catch (error) {
          console.warn(`Failed to fetch document type for ${diskPrefix}:`, error);
          return null;
        }
      });

      console.log('🔄 Dashboard: Waiting for document type API results...');
      const results = await Promise.all(typePromises);
      console.log('✅ Dashboard: Got document type API results:', results);

      const typeMap: Record<string, string> = {};

      results.forEach(result => {
        if (result && result.documentType) {
          typeMap[result.diskPrefix] = result.documentType;
        }
      });

      console.log('📊 Dashboard: Setting document type map:', typeMap);
      setDocumentTypesByDiskPrefix(typeMap);
    };

    fetchDocTypes();
  }, [signatureDocuments]);

  // Calculate counts based on status from signature documents
  const pendingSignatures = signatureDocuments.filter((doc: any) => {
    const apiStatus = documentStatuses[doc.document_id];
    const partyAssignment = doc.party_assignments && doc.party_assignments.length > 0 ? doc.party_assignments[0] : null;
    const status = apiStatus || partyAssignment?.status || doc.status;
    return status === 'pending';
  }).length;

  const completedDocuments = signatureDocuments.filter((doc: any) => {
    const apiStatus = documentStatuses[doc.document_id];
    const partyAssignment = doc.party_assignments && doc.party_assignments.length > 0 ? doc.party_assignments[0] : null;
    const status = apiStatus || partyAssignment?.status || doc.status;
    return status === 'completed' || status === 'signed';
  }).length;

  const totalDocuments = fileEntries.length;
  const verificationLevel = 'L1';

  // Handler functions for Wallet G-RADE buttons
  const handleRequestGrade = () => {
    setModalContent({
      title: 'Request G-RADE',
      message: 'The wallet grading system is coming soon! You\'ll be able to request a comprehensive analysis of your wallet\'s trust score, transaction history, and security rating.',
      icon: 'wallet'
    });
    setIsModalOpen(true);
  };

  const handleAddNewWallet = () => {
    setModalContent({
      title: 'Add New Wallet',
      message: 'Multi-wallet support is coming soon! You\'ll be able to connect and manage multiple wallets seamlessly within your dashboard.',
      icon: 'plus'
    });
    setIsModalOpen(true);
  };

  const handleVerifyWallet = () => {
    setModalContent({
      title: 'Verify Wallet',
      message: 'Enhanced wallet verification is coming soon! This feature will provide advanced authentication and security verification for your connected wallets.',
      icon: 'shield'
    });
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#f9fafb] p-[15px] md:p-[20px] font-sans">
      {/* Stats Cards */}
      <DashboardStats
        totalDocuments={totalDocuments}
        pendingSignatures={pendingSignatures}
        completedDocuments={completedDocuments}
        verificationLevel={verificationLevel}
      />

      {/* Alert Banner for Pending Signatures */}
      <AlertBanner pendingSignatures={pendingSignatures} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-20 md:gap-[20px] items-start">
        {/* LEFT COLUMN */}
        <div className="lg:col-span-2 flex flex-col gap-[15px] md:gap-[20px]">
          {/* Documents Table */}
          <DashboardCard title="Documents">
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-[14px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-12 py-12 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#Document Hash Key</th>
                    <th className="px-12 py-12 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                    <th className="px-12 py-12 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Author</th>
                    <th className="px-12 py-12 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Document Type</th>
                    <th className="px-12 py-12 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-12 py-12 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created At</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {fileEntries.length > 0 ? (
                    fileEntries.map((entry: any, index: number) => {
                      const docTypeRaw = entry?.minted_data?.[0]?.doc_name;
                      const matchedKey = docTypeRaw
                        ? Object.keys(docTypeMap).find(
                          key => key.toLowerCase() === String(docTypeRaw).toLowerCase(),
                        )
                        : undefined;
                      const docTypeDisplay = matchedKey ? docTypeMap[matchedKey] : docTypeRaw || '';
                      return (
                        <tr key={entry?.id || index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-12 py-12 text-sm font-mono text-gray-600">
                            {entry?.disk_prefix ? (
                              <span className="bg-gray-100 px-6 py-2 rounded text-xs">
                                {entry.disk_prefix}
                              </span>
                            ) : (
                              <span className="text-gray-400">N/A</span>
                            )}
                          </td>
                          <td className="px-12 py-12 text-sm font-medium text-gray-900">{entry?.name}</td>
                          <td className="px-12 py-12 text-sm text-gray-700">{entry?.author || 'N/A'}</td>
                          <td className="px-12 py-12">
                            <span className="inline-flex items-center px-8 py-4 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                              {entry?.document_type || docTypeDisplay || 'Unknown'}
                            </span>
                          </td>
                          <td className="px-12 py-12">
                            <span className={`inline-flex items-center px-6 py-2 rounded-full text-xs font-medium ${
                              entry?.verification === 1 ? 'bg-green-100 text-green-800 border border-green-200' :
                              entry?.verification === 0 ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                              'bg-gray-100 text-gray-800 border border-gray-200'
                            }`}>
                              {entry?.verification === 1 ? 'Verified' :
                               entry?.verification === 0 ? 'Pending' : 'Unknown'}
                            </span>
                          </td>
                          <td className="px-12 py-12 text-sm text-gray-600">
                            {entry?.created_at ? new Date(entry.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-12 py-32 text-center">
                        <div className="flex flex-col items-center justify-center text-gray-500">
                          <svg className="w-48 h-48 mb-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-sm font-medium">No documents available</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {fileEntries.length > 0 && fileEntries.length < 4 && (
              <div className="flex justify-end mt-16">
                <button
                  onClick={() => navigate('/documents')}
                  className="text-brand-purple hover:text-brand-pink font-medium text-14 transition-colors duration-200 flex items-center gap-4"
                >
                  View all <span>→</span>
                </button>
              </div>
            )}
          </DashboardCard>

          {/* Documents Needing Signature */}
          <DashboardCard title="Documents Needing Signature">
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-[14px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-12 py-12 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#Party ID</th>
                    <th className="px-12 py-12 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">#Document Hash Key</th>
                    <th className="px-12 py-12 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Document Type</th>
                    <th className="px-12 py-12 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Author</th>
                    <th className="px-12 py-12 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-12 py-12 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Created At</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {signatureDocuments.length > 0 ? (
                    signatureDocuments.slice(0, 5).map((doc: any, index: number) => {
                      const partyAssignment = doc.party_assignments && doc.party_assignments.length > 0
                        ? doc.party_assignments[0]
                        : null;
                      console.log('Dashboard - Processing doc:', { doc, partyAssignment, docTypeByPrefix: documentTypesByDiskPrefix[doc.docprefix] });
                      return (
                        <tr key={doc.document_id || index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-12 py-12 text-sm text-gray-700">
                            {(() => {
                              const displayPartyId = partyAssignment?.party_id || partyId || doc.party_id;
                              console.log('Dashboard - Party ID for doc:', doc.document_id, 'partyAssignment:', partyAssignment, 'partyId:', partyId, 'doc.party_id:', doc.party_id, 'display:', displayPartyId);
                              return displayPartyId ? (
                                <span className="font-mono text-xs bg-gray-100 px-6 py-2 rounded border border-gray-200">
                                  {displayPartyId.substring(0, 8)}...
                                </span>
                              ) : (
                                <span className="text-red-500">N/A</span>
                              );
                            })()}
                          </td>
                          <td className="px-12 py-12 text-sm font-mono text-gray-600">
                            {doc.docprefix ? (
                              <span className="bg-gray-100 px-6 py-2 rounded text-xs">
                                {doc.docprefix.substring(0, 8)}...
                              </span>
                            ) : (
                              <span className="text-gray-400">N/A</span>
                            )}
                          </td>
                          <td className="px-12 py-12">
                            <span className="inline-flex items-center px-8 py-4 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                              {(() => {
                                const diskPrefix = doc.docprefix || doc.disk_prefix;
                                const apiDocType = documentTypesByDiskPrefix[diskPrefix];
                                const fallbackDocType = doc.document_type;
                                const finalDocType = apiDocType || fallbackDocType || 'N/A';

                                console.log('🎯 Dashboard Document Type Debug:', {
                                  diskPrefix,
                                  apiDocType,
                                  fallbackDocType,
                                  finalDocType,
                                  docPrefix: doc.docprefix,
                                  docDiskPrefix: doc.disk_prefix,
                                  mapKeys: Object.keys(documentTypesByDiskPrefix)
                                });

                                return finalDocType;
                              })()}
                            </span>
                          </td>
                          <td className="px-12 py-12 text-sm text-gray-700">{doc.owner_name || 'N/A'}</td>
                          <td className="px-12 py-12">
                            <span className={`inline-flex items-center px-6 py-2 rounded-full text-xs font-medium ${
                              getStatusBadgeClasses(documentStatuses[doc.document_id] || partyAssignment?.status || doc.status)
                            }`}>
                              {getStatusDisplayText(documentStatuses[doc.document_id] || partyAssignment?.status || doc.status)}
                            </span>
                          </td>
                          <td className="px-12 py-12 text-sm text-gray-600">
                            {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-12 py-32 text-center">
                        <div className="flex flex-col items-center justify-center text-gray-500">
                          <svg className="w-48 h-48 mb-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                          <p className="text-sm font-medium">No documents requiring signature</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {templateData.length > 5 && (
              <div className="flex justify-end mt-16">
                <button
                  onClick={() => navigate('/signature-documents')}
                  className="text-brand-purple hover:text-brand-pink font-medium text-14 transition-colors duration-200 flex items-center gap-4"
                >
                  View all <span>→</span>
                </button>
              </div>
            )}
          </DashboardCard>

          {/* Witness Attestation */}
          <DashboardCard title="Witness Attestation">
            <div className="flex flex-col items-center justify-center py-32 text-gray-500">
              <svg className="w-64 h-64 mb-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <p className="text-sm font-medium">NO Documents here</p>
            </div>
          </DashboardCard>

          {/* Upload ID */}
          <DashboardCard title="Upload Government-issued Identification">
            <p className="text-gray-600 mb-[15px] text-[14px]">
              NO ID has been uploaded (Level 1 or Level 2 authorization disabled)
            </p>
            <ReusableButton>Upload</ReusableButton>
          </DashboardCard>
        </div>

        {/* RIGHT COLUMN */}
        <div className="lg:col-span-1 flex flex-col gap-[15px] md:gap-[20px]">
          {/* Wallet G-RADE */}
          <div className="rounded-2xl bg-gradient-to-br from-brand-purple via-brand-indigo to-brand-pink p-24 shadow-lg text-white">
            <div className="flex items-center space-x-8 mb-20">
              <svg className="w-24 h-24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <h2 className="text-20 font-semibold">Wallet G-RADE</h2>
            </div>
            <div className="flex flex-col gap-12">
              <button 
                onClick={handleRequestGrade}
                className="w-full bg-white text-brand-purple py-12 rounded-lg font-semibold text-14 hover:shadow-xl transition-all duration-200 hover:scale-105"
              >
                Request G-RADE
              </button>
              <button 
                onClick={handleAddNewWallet}
                className="w-full bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white py-12 rounded-lg font-semibold text-14 hover:bg-white/20 transition-all duration-200"
              >
                Add New Wallet
              </button>
              <button 
                onClick={handleVerifyWallet}
                className="w-full bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white py-12 rounded-lg font-semibold text-14 hover:bg-white/20 transition-all duration-200"
              >
                Verify Wallet
              </button>
            </div>
          </div>

          {/* Transactions */}
          <DashboardCard title="Transactions">
            <div className="space-y-12">
              <div className="flex items-center space-x-8">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-gray-400 text-xs">●</span>
                </div>
                <p className="text-gray-600 text-[14px]">No Current Transactions Active</p>
              </div>
              <div className="flex items-center space-x-8">
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-gray-400 text-xs">●</span>
                </div>
                <p className="text-gray-600 text-[14px]">No Requests for Level 2 authentication</p>
              </div>
            </div>
            <div className="mt-[20px] pt-[20px] border-t border-gray-100">
              <h3 className="text-[16px] font-semibold text-gray-800 mb-12">TX History</h3>
              <div className="flex flex-col items-center justify-center py-24 text-gray-500">
                <svg className="w-48 h-48 mb-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm">No history available</p>
              </div>
            </div>
          </DashboardCard>
        </div>
      </div>

      {/* Coming Soon Modal */}
      <ComingSoonModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalContent.title}
        message={modalContent.message}
        icon={modalContent.icon}
      />
    </div>
  );
};
